import React, { useState } from 'react';
import { useTechnologiesData } from '../context/TechnologiesContext';
import pb from '../lib/pocketbase';
import { FiCpu, FiPlus, FiTrash2, FiEdit2, FiMove, FiCheck, FiX, FiImage } from 'react-icons/fi';
import './Admin.css';

export default function AdminTechnologiesManager() {
    const { technologies, loading } = useTechnologiesData();
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        category: 'Frontend',
        description: '',
        order: 0,
        is_active: true
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const categories = ['Frontend', 'Backend', 'Data & Infra'];

    const handleEdit = (tech) => {
        setEditingId(tech.id);
        setFormData({
            name: tech.name,
            category: tech.category,
            description: tech.description || '',
            order: tech.order || 0,
            is_active: tech.is_active !== false
        });
        setSelectedFile(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({ name: '', category: 'Frontend', description: '', order: 0, is_active: true });
        setSelectedFile(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('category', formData.category);
            data.append('description', formData.description);
            data.append('order', formData.order);
            data.append('is_active', formData.is_active);
            if (selectedFile) {
                data.append('logo', selectedFile);
            }

            if (editingId && editingId !== 'new') {
                await pb.collection('technologies').update(editingId, data);
            } else {
                await pb.collection('technologies').create(data);
            }
            handleCancel();
        } catch (error) {
            alert("Erreur lors de la sauvegarde : " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Supprimer cette technologie ?')) {
            try {
                await pb.collection('technologies').delete(id);
            } catch (error) {
                alert("Erreur lors de la suppression.");
            }
        }
    };

    return (
        <div className="admin-container">
            <div className="admin-card">
                <div className="admin-card-header">
                    <div className="admin-card-title">
                        <FiCpu />
                        <h2>Gestion de la Stack Technique</h2>
                    </div>
                    {!editingId && (
                        <button className="btn btn-primary" onClick={() => setEditingId('new')}>
                            <FiPlus /> Ajouter une technologie
                        </button>
                    )}
                </div>

                {editingId && (
                    <div className="admin-form-box" style={{ marginBottom: '2rem', padding: '1.5rem', background: 'var(--admin-hover-bg)', borderRadius: '12px' }}>
                        <h3>{editingId === 'new' ? 'Nouvelle Technologie' : 'Modifier la Technologie'}</h3>
                        <form onSubmit={handleSubmit} className="admin-form">
                            <div className="admin-form-row">
                                <div className="admin-input-group">
                                    <label>Nom</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="admin-input-group">
                                    <label>Catégorie</label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    >
                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="admin-input-group" style={{ marginBottom: '1rem' }}>
                                <label>Description (courte)</label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Ex: Framework Frontend réactif"
                                />
                            </div>

                            <div className="admin-form-row">
                                <div className="admin-input-group">
                                    <label>Logo</label>
                                    <div className="file-input-wrapper">
                                        <input
                                            type="file"
                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                            accept="image/*"
                                        />
                                    </div>
                                </div>
                                <div className="admin-input-group">
                                    <label>Ordre d'affichage</label>
                                    <input
                                        type="number"
                                        value={formData.order}
                                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>

                            <div className="admin-actions" style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
                                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                    <FiCheck /> {isSaving ? 'Sauvegarde...' : 'Enregistrer'}
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                                    <FiX /> Annuler
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="admin-tech-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                    {categories.map(cat => (
                        <div key={cat} className="tech-category-group">
                            <h3 style={{ borderBottom: '2px solid var(--admin-primary)', paddingBottom: '0.5rem', marginBottom: '1rem' }}>{cat}</h3>
                            <div className="tech-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {technologies.filter(t => t.category === cat).map(tech => (
                                    <div key={tech.id} className="admin-item-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: 'var(--admin-card-bg)', border: '1px solid var(--admin-border)', borderRadius: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                            <div className="tech-logo-preview" style={{ width: '32px', height: '32px', background: '#fff', borderRadius: '4px', display: 'grid', placeItems: 'center', padding: '4px' }}>
                                                {tech.logo ? (
                                                    <img src={pb.files.getUrl(tech, tech.logo)} alt="" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                                ) : (
                                                    <FiImage style={{ color: '#ccc' }} />
                                                )}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '600' }}>{tech.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>{tech.description}</div>
                                            </div>
                                        </div>
                                        <div className="item-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button className="btn-icon" onClick={() => handleEdit(tech)}><FiEdit2 /></button>
                                            <button className="btn-icon delete" onClick={() => handleDelete(tech.id)}><FiTrash2 /></button>
                                        </div>
                                    </div>
                                ))}
                                {technologies.filter(t => t.category === cat).length === 0 && (
                                    <div style={{ fontSize: '0.85rem', fontStyle: 'italic', color: 'var(--admin-text-muted)', padding: '1rem', textAlign: 'center' }}>Aucune technologie</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
